import { useEffect, useMemo, useState } from 'react'
import { isString } from 'lodash'

import { scanRecipeCard } from '../../api/recipe'
import shrinkPhoto from '../../utils/shrinkPhoto'
import pdfToPhotos, { isPdf } from '../../utils/pdfToPhotos'

import './style.css'

export const TERMS_URL = 'https://getmorsel.com/terms-of-service/'

const MAX_PHOTOS = 3
const PHOTO_LABELS = ['Front', 'Back', 'Page 3']

const GENERIC_ERROR =
  "We couldn't read that recipe card. Please try again, or type the recipe in below."

const PDF_ERROR =
  "We couldn't open that PDF. If it has a password, remove it first, or use photos of the card."

// Pictures made from PDF pages; their thumbnails show the top of the page, where the title is
const pdfPages = new WeakSet()

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`

// Shown as a small tile until the first photo is taken. Photos are collected one at a
// time (a phone camera takes one per tap), and the card is only read when the cook
// presses "Read the recipe card". A PDF is turned into one photo per page.
// suggested: arrived from "Scan a recipe card" on the profile or in an email. A browser only
// opens the camera on a tap, so the tile is highlighted and brought into view instead.
const ScanRecipeCard = ({
  onScanned,
  onActiveChange = () => {},
  hidden = false,
  suggested = false,
}) => {
  const [photos, setPhotos] = useState([])
  const [status, setStatus] = useState('collecting')
  const [error, setError] = useState('')
  const [summary, setSummary] = useState(null)
  const [note, setNote] = useState('')

  const photoUrls = useMemo(() => photos.map((photo) => URL.createObjectURL(photo)), [photos])
  useEffect(() => () => photoUrls.forEach((url) => URL.revokeObjectURL(url)), [photoUrls])

  const onPhotosChosen = async (e) => {
    const files = Array.from(e.target.files || [])
    e.target.value = ''
    if (files.length === 0) {
      return
    }

    setStatus('preparing')
    setError('')
    setNote('')
    const added = []
    let skippedPages = false
    let pdfFailed = false
    for (const file of files) {
      const room = MAX_PHOTOS - photos.length - added.length
      if (room <= 0) {
        skippedPages = true
        break
      }
      if (!isPdf(file)) {
        added.push(await shrinkPhoto(file))
        continue
      }
      try {
        const { photos: pages, pageCount } = await pdfToPhotos(file, room)
        pages.forEach((page) => pdfPages.add(page))
        added.push(...pages)
        skippedPages = skippedPages || pageCount > pages.length
      } catch (err) {
        pdfFailed = true
      }
    }

    setPhotos([...photos, ...added])
    if (pdfFailed) {
      setError(PDF_ERROR)
    } else if (skippedPages) {
      setNote(
        `A recipe card can have up to ${MAX_PHOTOS} pages, so we kept the first ${MAX_PHOTOS}.`
      )
    }
    setStatus('collecting')
  }

  const removePhoto = (index) => {
    setPhotos(photos.filter((_, i) => i !== index))
    setError('')
    setNote('')
    setStatus('collecting')
  }

  const readCard = async () => {
    setStatus('reading')
    setError('')
    try {
      const { data } = await scanRecipeCard(photos)
      onScanned(data, photos)
      setSummary({
        ingredients: data?.ingredients?.length || 0,
        steps: data?.steps?.length || 0,
        readingProblems: data?.reading_problems,
      })
      setStatus('done')
    } catch (err) {
      const message = err?.response?.data?.data
      setError(isString(message) ? message : GENERIC_ERROR)
      setStatus('error')
    }
  }

  const startOver = () => {
    setPhotos([])
    setSummary(null)
    setError('')
    setNote('')
    setStatus('collecting')
  }

  // Tell the page when this tool is open, so it can hide the other "skip the typing" tile
  const isOpen = photos.length > 0 || status !== 'collecting' || Boolean(error)
  useEffect(() => onActiveChange(isOpen), [isOpen])

  if (hidden) {
    return null
  }

  const input = (
    <input
      id="scanRecipeCardInput"
      className="scanCardInput"
      type="file"
      accept="image/*,application/pdf,.pdf"
      multiple
      onChange={onPhotosChosen}
    />
  )

  if (!isOpen) {
    return (
      <>
        {input}
        <label
          className={`quickTile ${suggested ? 'quickTileSuggested' : ''}`}
          htmlFor="scanRecipeCardInput"
          ref={(tile) => suggested && tile?.scrollIntoView?.({ block: 'center' })}
        >
          <span className="quickTileIcon" aria-hidden="true">
            <CardScanArt />
          </span>
          <span className="quickTileTitle">Scan a card</span>
          <span className="quickTileText">
            Snap a photo or add a PDF. We'll type it up in seconds.
          </span>
        </label>
      </>
    )
  }

  if (status === 'preparing') {
    return (
      <div className="quickPanel">
        <div className="scanCardReading" role="status">
          <span className="scanCardSpinner" aria-hidden="true" />
          <p className="scanCardText">Getting your pages ready...</p>
        </div>
      </div>
    )
  }

  if (status === 'reading') {
    return (
      <div className="quickPanel">
        <div className="scanCardReading" role="status">
          <span className="scanCardSpinner" aria-hidden="true" />
          <p className="scanCardText">
            Reading your recipe card ({plural(photos.length, 'photo')}). This can take up to a
            minute.
          </p>
        </div>
      </div>
    )
  }

  if (status === 'done') {
    return (
      <div className="quickPanel quickPanelDone">
        <p className="scanCardTitle">
          <span className="quickDoneMark" aria-hidden="true">
            ✓
          </span>
          Filled in from your card
        </p>
        <p className="scanCardText">
          We found {plural(summary?.ingredients, 'ingredient')} and {plural(summary?.steps, 'step')}{' '}
          for the next steps, and your card photos are saved with the recipe. Give it all a quick
          check, handwriting can be tricky.
        </p>
        {summary?.readingProblems && (
          <p className="scanCardText scanCardNote">{summary.readingProblems}</p>
        )}
        <button type="button" className="scanCardLink" onClick={startOver}>
          Scan a different card
        </button>
      </div>
    )
  }

  return (
    <div className="quickPanel">
      {input}
      <p className="scanCardTitle">Your recipe card</p>
      <p className="scanCardText">
        {photos.length < MAX_PHOTOS
          ? 'Writing on the back, or another page? Add it too. Then read the card.'
          : 'All set. Read the card when you are ready.'}
      </p>

      <div className="scanCardPhotos">
        {photoUrls.map((url, index) => (
          <div className="scanCardPhoto" key={url}>
            <img
              className={`scanCardPhotoImage${
                pdfPages.has(photos[index]) ? ' scanCardPdfPage' : ''
              }`}
              src={url}
              alt={PHOTO_LABELS[index]}
            />
            <span className="scanCardPhotoLabel">{PHOTO_LABELS[index]}</span>
            <button
              type="button"
              className="scanCardRemove"
              aria-label={`Remove ${PHOTO_LABELS[index]}`}
              onClick={() => removePhoto(index)}
            >
              ×
            </button>
          </div>
        ))}
        {photos.length < MAX_PHOTOS && (
          <label className="scanCardAddTile" htmlFor="scanRecipeCardInput">
            <span aria-hidden="true">+</span>
            Add page
          </label>
        )}
      </div>

      {note && <p className="scanCardText scanCardNote">{note}</p>}

      {error && (
        <p className="scanCardText scanCardError" role="alert">
          {error}
        </p>
      )}

      <div className="scanCardActions">
        <button
          type="button"
          className="scanCardButton"
          onClick={readCard}
          disabled={photos.length === 0}
        >
          {status === 'error' ? 'Try reading it again' : 'Read the recipe card'}
        </button>
        <button type="button" className="scanCardLink" onClick={startOver}>
          Cancel
        </button>
      </div>
    </div>
  )
}

// A recipe card inside camera corners, in Morsel colors
export const CardScanArt = () => (
  <svg viewBox="0 0 64 64" width="56" height="56">
    <path
      d="M4 14V8a4 4 0 0 1 4-4h6M50 4h6a4 4 0 0 1 4 4v6M60 50v6a4 4 0 0 1-4 4h-6M14 60H8a4 4 0 0 1-4-4v-6"
      fill="none"
      stroke="#57ad91"
      strokeWidth="3"
      strokeLinecap="round"
    />
    <g transform="rotate(-8 32 32)">
      <rect
        x="12"
        y="17"
        width="40"
        height="30"
        rx="3"
        fill="#fffdf2"
        stroke="#37190c"
        strokeWidth="2.5"
      />
      <path d="M12 24.5h40" stroke="#d9483b" strokeWidth="1.8" />
      <path
        d="M17 31c3-2 5 2 8 0s5 2 8 0 5 2 8 0M17 38c3-2 5 2 8 0s5 2 8 0"
        fill="none"
        stroke="#37190c"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </g>
  </svg>
)

export default ScanRecipeCard
